/**
 * purchase-group-by-utils.ts
 *
 * Grouping logic and subtotal calculations for the Purchases / Commitments tab.
 *
 * Supported group-by fields (categorical / low-cardinality only):
 *   - status           (PurchaseRequest.status)
 *   - supplier         (PurchaseRequest.supplier — resolved display name)
 *   - category         (PurchaseRequest.category)
 *   - cost-centre      (PurchaseRequest.costCentreCode / costCentreName)
 *   - requested-by     (PurchaseRequest.requestedBy — resolved display name)
 *
 * Subtotals per group:
 *   - count            number of requests
 *   - totalAmount      sum of amounts across all requests in group
 *   - pendingAmount    sum of amounts for in-flight requests (Pending / Reopened / Redirected)
 *   - approvedAmount   sum of amounts for Approved / Posted requests
 *   - rejectedAmount   sum of amounts for Rejected / Cancelled requests
 */

import type { PurchaseRequest } from '@/types/purchase-request';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type PRGroupById =
  | 'status'
  | 'supplier'
  | 'category'
  | 'cost-centre'
  | 'requested-by';

export interface PRGroupByOption {
  id: PRGroupById;
  label: string;
}

export const PR_GROUP_BY_OPTIONS: PRGroupByOption[] = [
  { id: 'status',         label: 'Status' },
  { id: 'supplier',       label: 'Supplier' },
  { id: 'category',       label: 'Category' },
  { id: 'cost-centre',    label: 'Cost Centre' },
  { id: 'requested-by',   label: 'Requested By' },
];

export interface PRGroupSubtotals {
  count: number;
  totalAmount: number;
  pendingAmount: number;
  approvedAmount: number;
  rejectedAmount: number;
}

export interface PurchaseRequestGroup {
  /** Stable key used for collapse state */
  key: string;
  /** Human-readable label shown in the group header */
  label: string;
  /** The PRGroupById field this group was created from */
  groupById: PRGroupById;
  requests: PurchaseRequest[];
  subtotals: PRGroupSubtotals;
}

// ─── Status helpers ────────────────────────────────────────────────────────────

const IN_FLIGHT_STATUSES  = new Set(['Pending', 'Reopened', 'Redirected']);
const APPROVED_STATUSES   = new Set(['Approved', 'Posted']);
const REJECTED_STATUSES   = new Set(['Rejected', 'Cancelled']);

// ─── Key extraction ────────────────────────────────────────────────────────────

export function getPRGroupKey(
  request: PurchaseRequest,
  groupBy: PRGroupById,
  resolveEmployeeName: (id: string) => string,
): string {
  switch (groupBy) {
    case 'status':
      return request.status ?? '__none__';

    case 'supplier':
      return request.supplierName || resolveEmployeeName(request.supplier) || request.supplier || '__none__';

    case 'category':
      return request.category || '__none__';

    case 'cost-centre':
      return request.costCentreCode
        ? `${request.costCentreCode}${request.costCentreName ? ' — ' + request.costCentreName : ''}`
        : '__none__';

    case 'requested-by':
      return resolveEmployeeName(request.requestedBy) || request.requestedBy || '__none__';

    default:
      return '__none__';
  }
}

function getGroupLabel(key: string, groupBy: PRGroupById): string {
  if (key === '__none__') {
    switch (groupBy) {
      case 'cost-centre':   return 'No Cost Centre';
      default:              return 'Unassigned';
    }
  }
  return key;
}

// ─── Subtotal calculation ──────────────────────────────────────────────────────

function calcSubtotals(requests: PurchaseRequest[]): PRGroupSubtotals {
  return requests.reduce<PRGroupSubtotals>(
    (acc, r) => {
      acc.count         += 1;
      acc.totalAmount   += r.amount;
      acc.pendingAmount  += IN_FLIGHT_STATUSES.has(r.status)  ? r.amount : 0;
      acc.approvedAmount += APPROVED_STATUSES.has(r.status)   ? r.amount : 0;
      acc.rejectedAmount += REJECTED_STATUSES.has(r.status)   ? r.amount : 0;
      return acc;
    },
    { count: 0, totalAmount: 0, pendingAmount: 0, approvedAmount: 0, rejectedAmount: 0 },
  );
}

// ─── Sort order for group headers ─────────────────────────────────────────────

// Status sort: in-flight first, then approved/posted, then rejected/cancelled, then draft
const STATUS_ORDER: Record<string, number> = {
  Pending:    0,
  Reopened:   1,
  Redirected: 2,
  Draft:      3,
  Approved:   4,
  Posted:     5,
  Rejected:   6,
  Cancelled:  7,
};

function groupSortKey(group: PurchaseRequestGroup, groupBy: PRGroupById): string {
  if (group.key === '__none__') return 'zzz';
  switch (groupBy) {
    case 'status':
      return String(STATUS_ORDER[group.key] ?? 99).padStart(2, '0');
    default:
      return group.label.toLowerCase();
  }
}

// ─── Main grouping function ────────────────────────────────────────────────────

/**
 * Buckets an array of PurchaseRequest objects into groups for the given Group By field.
 */
export function groupPurchaseRequests(
  requests: PurchaseRequest[],
  groupBy: PRGroupById,
  resolveEmployeeName: (id: string) => string,
): PurchaseRequestGroup[] {
  const map = new Map<string, PurchaseRequest[]>();

  for (const request of requests) {
    const key = getPRGroupKey(request, groupBy, resolveEmployeeName);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(request);
  }

  const groups: PurchaseRequestGroup[] = [];

  for (const [rawKey, groupRequests] of map.entries()) {
    groups.push({
      key: rawKey,
      label: getGroupLabel(rawKey, groupBy),
      groupById: groupBy,
      requests: groupRequests,
      subtotals: calcSubtotals(groupRequests),
    });
  }

  groups.sort((a, b) =>
    groupSortKey(a, groupBy).localeCompare(groupSortKey(b, groupBy)),
  );

  return groups;
}
